import React, {
	forwardRef,
	useCallback,
	useContext,
	useEffect,
	useImperativeHandle,
	useMemo,
	useRef,
	useState,
} from "react";
import ReactDOM from "react-dom";
import { commitMutation, graphql, useFragment } from "react-relay";
import { toast } from "react-toastify";

import { faEye } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";
import moment from "moment";
import { v4 as uuidv4 } from "uuid";

import Attachments, { AttachmentsRelay } from "~/attachments/AttachmentsRelay";
import { AppContext } from "~/common";
import { confirm } from "~/lib/prompts";
import { EditorConfig, ProseReact } from "~/lib/prose";
import { getAllMarks } from "~/lib/prose/marks";
import {
	BulletPoint,
	CustomEmoji,
	Embed,
	HardBreak,
	HorizontalRule,
	KeyResult,
	Mention,
	NumberedListItem,
	Tag,
	TextNode,
	Unstyled,
} from "~/lib/prose/nodes";
import { Header as HeaderNode } from "~/lib/prose/nodes/header";
import { ImageStrip, ImageStripItem, ImageStripItemCaption, ImageStripItemFile } from "~/lib/prose/nodes/image-strip";
import {
	Agenda,
	BlockCreator,
	Collab,
	DropTarget,
	ExternalAttribute,
	GlobalPlaceholder,
	Indent,
	InputRules,
	MentionProvider,
	ModS,
	MoveSelection,
	MultiSelect,
	MultiSelectDropHandler,
	Paste,
	SlashProvider,
	Todo,
	TypeAhead,
} from "~/lib/prose/plugins";
import ActionItemProgressPlugin from "~/lib/prose/plugins/actionItemProgress";
import { BlockActions } from "~/lib/prose/plugins/blockActions";
import BlockAnnotation from "~/lib/prose/plugins/blockAnnotation";
import BlockBoundary, { getBlockBoundaryState } from "~/lib/prose/plugins/blockBoundary";
import type { StatusChange } from "~/lib/prose/plugins/collab";
import { CollapseExtension, getCollapsedAncestors, setCollapsedMany } from "~/lib/prose/plugins/collapse";
import {
	FellowAttachmentHandler,
	FileDropHandler,
	ImageStripItemHandler,
	NoteTaskDropHandler,
} from "~/lib/prose/plugins/dropTarget";
import EmojiProvider, { PasteBuiltInEmoji } from "~/lib/prose/plugins/emoji";
import FilePaste from "~/lib/prose/plugins/filePaste";
import FocusArea from "~/lib/prose/plugins/focusArea";
import { Keyboard } from "~/lib/prose/plugins/keyboard";
import MousePos from "~/lib/prose/plugins/mousePos";
import SelectedBlock from "~/lib/prose/plugins/selectedBlock";
import TagProvider from "~/lib/prose/plugins/tag";
import Toolbar, { type ToolbarProps } from "~/lib/prose/plugins/toolbar";
import { commitMutationPromise, decodeGlobalId, useLazyLoadQuery } from "~/lib/relayUtil";
import { dispatchAnalyticsTrack } from "~/lib/segment";
import { environment } from "~/models";
import { formatLastUpdatedDate, noteIsEmpty } from "~/stream/utils";
import { Button } from "~/ui";

import type { PrivateNoteSidePanelChangeTitleMutation } from "~/__generated__/PrivateNoteSidePanelChangeTitleMutation.graphql";
import type { PrivateNoteSidePanelDeleteMutation } from "~/__generated__/PrivateNoteSidePanelDeleteMutation.graphql";
import type { PrivateNoteSidePanelFragment$key } from "~/__generated__/PrivateNoteSidePanelFragment.graphql";
import type { PrivateNoteSidePanelQuery } from "~/__generated__/PrivateNoteSidePanelQuery.graphql";
import type { PrivateNotesPanelListQuery$data } from "~/__generated__/PrivateNotesPanelListQuery.graphql";

import styles from "./PrivateNote.module.css";

const privateNoteQuery = graphql`
	query PrivateNoteSidePanelQuery($id: ID!) {
		privateNote: node(id: $id) {
			...PrivateNoteSidePanelFragment
		}
	}
`;

const privateNoteFragment = graphql`
	fragment PrivateNoteSidePanelFragment on PrivateNote {
		id
		title
		updatedAt
		note {
			id
			collapsedBlockKeys
			rawDraftJson
			updatedAt
			...AttachmentsRelay_note
			attachments {
				id
				dimensions {
					height
					width
				}
				...AttachmentPreviewAttachment
			}
		}
		linkedNote {
			url
			eventStart
			createdAt
		}
	}
`;

const changePrivateNoteTitleMutation = graphql`
	mutation PrivateNoteSidePanelChangeTitleMutation($privateNoteId: ID!, $title: String!) {
		changePrivateNoteTitle(privateNoteId: $privateNoteId, title: $title) {
			privateNote {
				id
				title
			}
		}
	}
`;

const deletePrivateNoteMutation = graphql`
	mutation PrivateNoteSidePanelDeleteMutation($privateNoteId: ID!) {
		deletePrivateNote(privateNoteId: $privateNoteId) {
			stream {
				privateNotes {
					id
				}
			}
		}
	}
`;

const MENU_EXCLUDE_CHOICES = ["Jira", "ClickUp", "Trello", "monday.com", "Linear", "Asana", "Microsoft To Do", "Tag"];

const excludeChoiceFromMenu: ToolbarProps["excludeChoice"] = choice =>
	MENU_EXCLUDE_CHOICES.some(c => c === choice.title) || choice.title.startsWith("Section ");

export interface PrivateNoteRef {
	title: string | null;
	onUpdateTitle: (title: string, fromPrompt?: boolean) => void;
	onDelete: () => Promise<boolean>;
}

interface PrivateNoteRendererProps {
	privateNoteKey: PrivateNoteSidePanelFragment$key;
	viewer: PrivateNotesPanelListQuery$data["viewer"];
	isVisible: boolean;
	setIsVisible: (isVisible: boolean) => void;
	initiallyHighlightedBlockKey?: string;
	onNoteLinkIconClick?(): void;
	broadcastDeltaBackToSender?: boolean;
	className?: string;
}

export const PrivateNoteRenderer = forwardRef<PrivateNoteRef, PrivateNoteRendererProps>(
	(
		{
			privateNoteKey,
			viewer,
			isVisible,
			setIsVisible,
			initiallyHighlightedBlockKey,
			broadcastDeltaBackToSender,
			className,
		},
		ref,
	) => {
		const privateNote = useFragment<PrivateNoteSidePanelFragment$key>(privateNoteFragment, privateNoteKey);
		const { user, account } = useContext(AppContext);
		const [attachmentsContainer, setAttachmentsContainer] = useState<HTMLElement>();
		const [attachmentDropzoneRef, setAttachmentDropzoneRef] = useState<AttachmentsRelay>();
		const [collabLastSaved, setCollabLastSaved] = useState<number>(0);
		const editorRef = useRef<ProseReact>(null);
		const containerRef = useRef<HTMLDivElement>(null);

		const ensureBlockAncestorsExpanded = useCallback((key: string) => {
			const view = editorRef.current?.editorView;
			if (!view) return;
			const blockBoundary = getBlockBoundaryState(view.state);
			if (!blockBoundary || !blockBoundary.blockBoundaries[key]) return;
			const [blockPos] = blockBoundary.blockBoundaries[key];
			const blockNode = view.state.doc.nodeAt(blockPos);
			if (!blockNode?.attrs.blockId) return;

			const collapsedAncestors = getCollapsedAncestors(view.state, blockNode);
			if (!collapsedAncestors.length) return;
			setCollapsedMany(view, collapsedAncestors, false);
		}, []);

		const highlightBlock = useCallback(
			(key: string) => {
				const highlight = (remainingTries = 3) => {
					const block = containerRef.current?.querySelector(`[data-block-id="${key}"]`);
					if (!(block instanceof HTMLElement)) {
						if (remainingTries <= 0) return;
						window.setTimeout(() => highlight(remainingTries - 1), 50);
						return;
					}
					ensureBlockAncestorsExpanded(key);
					block.scrollIntoView({ behavior: "smooth", block: "center" });
					block.classList.add(styles.highlightedBlock);
					setTimeout(() => {
						block.classList.remove(styles.highlightedBlock);
					}, 5000);
				};
				window.setTimeout(() => highlight(), 0);
			},
			[ensureBlockAncestorsExpanded],
		);

		useEffect(() => {
			if (!initiallyHighlightedBlockKey) return;
			highlightBlock(initiallyHighlightedBlockKey);
		}, [initiallyHighlightedBlockKey, highlightBlock]);

		const onUpdateTitle = useCallback(
			(newTitle: string, fromPrompt?: boolean) => {
				if (newTitle.length > 255) return;
				commitMutation<PrivateNoteSidePanelChangeTitleMutation>(environment, {
					mutation: changePrivateNoteTitleMutation,
					variables: {
						privateNoteId: privateNote.id,
						title: newTitle,
					},
				});
				dispatchAnalyticsTrack("Rename private note", {
					feature: "private-notes",
					from: fromPrompt ? "side-bar" : "view-all",
				});
			},
			[privateNote.id],
		);

		const onEditorStatusChange = useCallback((e: CustomEvent<StatusChange>) => {
			setCollabLastSaved(e.detail.lastSaveTime);
		}, []);

		const getLastUpdatedString = useCallback(() => {
			if (!privateNote.note) return;
			const lastUpdatedDate = moment(collabLastSaved || privateNote.note.updatedAt || "");
			return formatLastUpdatedDate(lastUpdatedDate);
		}, [privateNote.note, collabLastSaved]);

		const collab = React.useMemo(() => {
			const newCollab = new Collab({
				documentId: privateNote.note.id,
				sessionId: window.CLIENT_SESSION_ID,
				userId: decodeGlobalId(user?.id ?? "")[1],
				initialData: privateNote.note.rawDraftJson,
				broadcastDeltaBackToSender,
			});
			newCollab.addEventListener("statuschange", onEditorStatusChange as EventListener);
			return newCollab;
		}, [user, privateNote.note, onEditorStatusChange, broadcastDeltaBackToSender]);

		useEffect(() => {
			return () => {
				if (!collab) return;
				collab.removeEventListener("statuschange", onEditorStatusChange as EventListener);
			};
		}, [collab, onEditorStatusChange]);

		const externalAttribute = useMemo(() => {
			return new ExternalAttribute({
				documentId: privateNote.note.id,
				sessionId: window.CLIENT_SESSION_ID,
				initialData: privateNote.note.rawDraftJson,
			});
		}, [privateNote.note]);

		const clickToShow = useCallback(() => {
			editorRef.current?.focus();
			setIsVisible(true);
			dispatchAnalyticsTrack("Unblur private note", { feature: "private-notes" });
		}, [editorRef, setIsVisible]);

		useEffect(() => {
			if (!editorRef.current || !editorRef.current.editorView) return;
			if (!noteIsEmpty(editorRef.current.editorView.state)) return;
			setIsVisible(true);
		}, [editorRef, setIsVisible]);

		const onDelete = useCallback(async () => {
			if (
				!(await confirm(
					<>
						Are you sure you want to delete <strong>{privateNote.title ?? "Untitled"}</strong>? This operation is
						irreversible.
					</>,
					{
						yesText: "Delete",
						yesProps: { variant: "destructive" },
						title: "Delete private note?",
					},
				))
			)
				return false;
			dispatchAnalyticsTrack("Delete private note", { feature: "private-notes" });
			let result;
			try {
				result = await commitMutationPromise<PrivateNoteSidePanelDeleteMutation>(environment, {
					mutation: deletePrivateNoteMutation,
					variables: {
						privateNoteId: privateNote.id,
					},
				});
			} catch (err) {
				toast.error("Failed to delete private note");
				return false;
			}
			return Boolean(result?.deletePrivateNote?.stream);
		}, [privateNote.id, privateNote.title]);

		const onUploadFiles = useCallback(
			(files: File[]) => {
				dispatchAnalyticsTrack("Private notes paste file", { feature: "private-notes" });
				attachmentDropzoneRef?.upload(files);
			},
			[attachmentDropzoneRef],
		);

		// Using a useRef because React promises not to change it, whereas useMemo
		// makes no such promise.
		const toolbarUUID = useRef<string>();
		if (toolbarUUID.current === undefined) toolbarUUID.current = uuidv4();

		const toolbarMountPointId = useMemo(
			() => `private-note-${privateNote.id}-toolbar-${toolbarUUID.current}`,
			[privateNote.id],
		);

		const config = React.useMemo(() => {
			return new EditorConfig({
				nodes: { doc: { content: "block+" } },
				extensions: [
					...getAllMarks({ linkEditable: true, placeholderEditable: false, linkToolBarAllowEmbeds: false }),
					new BlockActions(),
					new BlockBoundary(),
					// CollapseExtension must come after BlockBoundary since it uses
					// blockBoundary's plugin state to initialize its own
					new CollapseExtension(privateNote.note.collapsedBlockKeys),
					new Keyboard(),
					new TextNode(),
					new Indent(),
					new Unstyled(),
					new BulletPoint(),
					new NumberedListItem(),
					new ImageStrip({
						contentId: privateNote.note.id,
						disableWhenNotEditable: true,
						hidePreview: false,
						initialDimensions: privateNote.note.attachments
							.filter(Boolean)
							.reduce((acc, attachment) => ({ ...acc, [attachment.id]: attachment.dimensions || undefined }), {}),
					}),
					new ImageStripItem(),
					new ImageStripItemFile(),
					new ImageStripItemCaption(),
					new HardBreak(),
					new HorizontalRule(),
					new HeaderNode({ promptDisabled: true }),
					new Mention({ currentUserId: user?.id }),
					new Tag({
						showToolbar: !(user?.isGuest ?? true),
						allowEditing: !(user?.isGuest ?? true),
						clickable: !(user?.isGuest ?? true),
					}),
					new CustomEmoji({ accountId: account?.id }),
					new Toolbar({
						setAttachmentsRef: setAttachmentsContainer,
						isExternalUser: false,
						excludeChoice: excludeChoiceFromMenu,
						mountPointId: toolbarMountPointId,
						className: styles.toolbar,
					}),
					new DropTarget({
						handlers: [
							new NoteTaskDropHandler(),
							new MultiSelectDropHandler(),
							new FileDropHandler(onUploadFiles),
							new FellowAttachmentHandler(),
							new ImageStripItemHandler(),
						],
					}),
					new MultiSelect(),
					new MousePos(),
					new MoveSelection(),
					new ModS(),
					new FilePaste({ upload: onUploadFiles }),
					new BlockCreator({ userId: user?.id }),
					new FocusArea({ getTarget: (dom: HTMLElement) => dom.closest(".notes-stream-card")?.querySelector(".main") }),
					new Todo({
						note: privateNote.note,
						enableCopiedItemPopup: true,
						disableWhenNotEditable: true,
						disabledReadOnly: false,
						viewerId: user?.id,
					}),
					new Agenda({
						note: privateNote.note,
						enableCopiedItemPopup: true,
						employeeGuidanceShowManager: false,
						disabledReadOnly: false,
						viewerId: user?.id,
					}),
					new TypeAhead({
						providers: [
							new SlashProvider({
								isExternalUser: false,
								exclude: [
									"Jira",
									"Record a video",
									"ClickUp",
									"Trello",
									"Asana",
									"monday.com",
									"Linear",
									"Microsoft To Do",
									"Section (Talking Points)",
									"Section (Action Items)",
									"Section (Custom)",
								],
							}),
							new MentionProvider(),
							new EmojiProvider(),
							new TagProvider({ viewerIsGuest: user?.isGuest ?? false }),
						],
					}),
					new ActionItemProgressPlugin(),
					new Paste(),
					new PasteBuiltInEmoji(),
					new GlobalPlaceholder({
						text: "Use this personal space to record your thoughts, draft an agenda, add private action items...",
					}),
					new SelectedBlock(),
					new InputRules(),
					new Embed({ renderFrame: true }),
					new KeyResult(),
					collab,
					externalAttribute,
					new BlockAnnotation(),
				],
			});
		}, [
			privateNote.note,
			user?.id,
			user?.isGuest,
			account?.id,
			toolbarMountPointId,
			onUploadFiles,
			collab,
			externalAttribute,
		]);

		useImperativeHandle(ref, () => ({
			title: privateNote.title,
			onUpdateTitle,
			onDelete,
		}));

		return (
			<article className={cx(styles.privateNote, className)} data-private-note-id={privateNote.id} ref={containerRef}>
				{!isVisible ? (
					<div className={styles.hideNote} onClick={clickToShow}>
						<div className={cx(styles.mockBar, styles.mockBarShort, styles.mockBarThick)}>&nbsp;</div>
						<div className={cx(styles.mockBar, styles.mockBarLong)}>&nbsp;</div>
						<div className={styles.mockBar}>&nbsp;</div>
						<div className={cx(styles.mockBar, styles.mockBarLong)}>&nbsp;</div>

						<div className={styles.mockBlock}>
							<div className={cx(styles.icon, styles.mockBar)}>&nbsp;</div>
							<div className={cx(styles.longBlock, styles.mockBar, styles.mockBarLong)}>&nbsp;</div>
							<div className={cx(styles.shortBlock, styles.mockBar, styles.mockBarMedium)}>&nbsp;</div>
						</div>

						<div className={styles.mockBlock}>
							<div className={cx(styles.icon, styles.mockBar)}>&nbsp;</div>
							<div className={cx(styles.longBlock, styles.mockBar, styles.mockBarLong)}>&nbsp;</div>
							<div className={cx(styles.shortBlock, styles.mockBar, styles.mockBarMedium)}>&nbsp;</div>
						</div>

						<div className={styles.blurOverlay}>
							<div className={styles.buttonWrapper}>
								<Button className={styles.toggleVisibilityButton}>
									<FontAwesomeIcon icon={faEye} fixedWidth />
									Reveal Notes
								</Button>
							</div>
						</div>
					</div>
				) : null}
				<div id={toolbarMountPointId} className={styles.toolbarContainer} />
				<ProseReact
					ref={editorRef}
					initialDoc={privateNote.note.rawDraftJson.doc}
					config={config}
					context={{
						getLastUpdatedString,
						// TODO: We should improve this
						// I don't want to get this from relay because we don't need to query the participants
						// For private notes, so we are making a "fake" participant for the user here
						participants: [{ ...user, user: { ...user, isActive: true } }],
						noteId: privateNote.note.id,
						userId: user?.id,
						billing: viewer.account.billingAccount,
						allowCompleteByAny: viewer.account.allowCompleteByAny,
						tags: viewer.account.tags,
						isPrivateNote: true,
					}}
					key={privateNote.id}
					className={styles.editor}
					enableReactHost
					focusOnMount={false}
				/>
				{attachmentsContainer &&
					ReactDOM.createPortal(
						<Attachments
							componentRef={setAttachmentDropzoneRef}
							groupId={privateNote.note.id}
							note={privateNote.note}
						/>,
						attachmentsContainer,
					)}
			</article>
		);
	},
);

PrivateNoteRenderer.displayName = "PrivateNoteRenderer";

interface PrivateNoteProps extends Omit<PrivateNoteRendererProps, "privateNoteKey"> {
	id: string;
}

const PrivateNote = forwardRef<PrivateNoteRef, PrivateNoteProps>(({ id, ...props }, ref) => {
	const data = useLazyLoadQuery<PrivateNoteSidePanelQuery>(privateNoteQuery, { id }, {});

	if (!data.privateNote) return <></>;

	return <PrivateNoteRenderer privateNoteKey={data.privateNote} {...props} ref={ref} />;
});

PrivateNote.displayName = "PrivateNote";

export default PrivateNote;
